import { useEffect, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { addYears, isAfter, isBefore, startOfDay } from 'date-fns';

import { dateToApiFormat } from '@epi-helpers/dateHelper';
import { FormFields } from '@epi-forms/helpers';
import { disableSaveButtonForBeneficiariesDataModal } from '@epi-helpers/BeneficiaryHelper';

export const useEndOfFundsDateDialog = (handleClose: () => void) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const [date, setDate] = useState<null | Date>(null);
  const endOfBenefit = watch(FormFields.EndOfBenefit);

  const {
    field: { value: endDate, onChange }
  } = useController({
    name: FormFields.EndOfFunds,
    control
  });

  const handleSave = () => {
    if (date === null) {
      onChange('');
      handleClose();
      return;
    }
    onChange(dateToApiFormat(date));
    handleClose();
  };

  const handleSetDate = (value: Date | null) => setDate(value);

  useEffect(() => {
    if (endDate) {
      setDate(new Date(endDate));
    }
  }, [endDate, endOfBenefit]);

  const errorText = useMemo(() => {
    if (date === null || !endOfBenefit) {
      return undefined;
    }

    const benefitEnd = startOfDay(new Date(endOfBenefit));
    const selected = startOfDay(date);
    const maxDate = startOfDay(addYears(benefitEnd, 2));

    if (isBefore(selected, benefitEnd)) {
      return t(
        'containers.beneficiaries.detail.end_of_founds_before_benefit_termination_err_msg'
      );
    }

    if (isAfter(selected, maxDate)) {
      return t(
        'containers.beneficiaries.detail.end_of_founds_exceed_two_years_err_msg'
      );
    }

    return undefined;
  }, [date, endOfBenefit]);

  const disableSave =
    date !== null &&
    (!!errorText || disableSaveButtonForBeneficiariesDataModal(date));

  return {
    handleSetDate,
    handleSave,
    endOfBenefit: endDate,
    disableSave,
    errorText
  };
};
